import Api from "@/services/Api";

let timer = null;
// Epoch-ms estimate of when the current access token expires, derived from the last
// ping's expires_in. Used to decide whether a tab-focus actually needs a ping.
let predictedExpiryMs = 0;

// Ping this many ms before expiry — well inside the server's 120s refresh window so
// the ping itself triggers a proactive token refresh (via InjectSessionToken →
// ensureFreshAccessToken) before any user-initiated burst can race the boundary.
const LEAD_MS = 90_000;
const FALLBACK_INTERVAL_MS = 8 * 60_000; // used when the server returns no expiry
const MIN_DELAY_MS = 5_000;

async function ping() {
  // If the session was torn down (e.g. the Api interceptor force-logged-out on a
  // persistent 401, or the user logged out), stop instead of pinging the login page.
  if (!localStorage.getItem("user")) {
    stopKeepAlive();
    return;
  }

  try {
    const res = await Api().get("/session/ping");
    schedule(res?.data);
  } catch {
    // A 401 is already handled by the Api() interceptor (retry → logout if persistent).
    // For any other transient error, retry on the fixed fallback — unless we were just
    // logged out, in which case stop.
    if (localStorage.getItem("user")) {
      schedule(null);
    } else {
      stopKeepAlive();
    }
  }
}

function schedule(data) {
  clearTimeout(timer);

  let delayMs;
  if (data && typeof data.expires_in === "number") {
    // Preferred: seconds-remaining, immune to client clock skew.
    predictedExpiryMs = Date.now() + data.expires_in * 1000;
    delayMs = data.expires_in * 1000 - LEAD_MS;
  } else if (data && data.expires_at) {
    // Fallback: absolute timestamp (clock-skew sensitive).
    predictedExpiryMs = new Date(data.expires_at).getTime();
    delayMs = predictedExpiryMs - Date.now() - LEAD_MS;
  } else {
    // No expiry returned — re-arm on the fixed fallback and keep the prediction
    // conservative so a focus event still re-checks soon.
    predictedExpiryMs = Date.now() + FALLBACK_INTERVAL_MS + LEAD_MS;
    delayMs = FALLBACK_INTERVAL_MS;
  }

  timer = setTimeout(ping, Math.max(delayMs, MIN_DELAY_MS));
}

function onVisibilityChange() {
  if (document.visibilityState !== "visible") return;

  // Background timers get throttled/suspended while the tab is hidden, so on focus we
  // either ping now (if the token is at/near expiry — the idle case) or simply re-arm
  // the timer for the remaining time. This avoids hammering /session/ping on every
  // casual tab switch while still guaranteeing a fresh token before the user interacts.
  clearTimeout(timer);
  if (!predictedExpiryMs || Date.now() >= predictedExpiryMs - LEAD_MS) {
    ping();
  } else {
    timer = setTimeout(ping, Math.max(predictedExpiryMs - Date.now() - LEAD_MS, MIN_DELAY_MS));
  }
}

export function startKeepAlive() {
  clearTimeout(timer);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  document.addEventListener("visibilitychange", onVisibilityChange);
  ping(); // seeds the schedule and triggers the first proactive refresh if needed
}

export function stopKeepAlive() {
  clearTimeout(timer);
  timer = null;
  predictedExpiryMs = 0;
  document.removeEventListener("visibilitychange", onVisibilityChange);
}
